import React from "react";

const Article = () => {
  return (
    <div className="mb-20">
      <div>
        <h2 id="ec20_e">EC20</h2>
        <hr className="dividergeneral mb-5"></hr>

        <p className="mb-2">
          EC20 is a synthetic phytochelatin with a high affinity for cadmium
          ions and, importantly, a higher affinity than for other bivalent
          cations such as zinc. First synthetized by W. Bae & al. 2000
          <a href="#reference1">
            <sup>1</sup>
          </a>{" "}
          it is composed of 20 repetitions of the amino acids pair Glutamic acid
          plus Cysteine, and a terminal Glycine. We made use of its binding
          capacities to catch and trap cadmium ions at the surface of our live
          biotherapeutic product.
        </p>

        <p className="mb-2">Alphafold predicted EC20 structure</p>

        <figure className="flex flex-col justify-center items-center gap-4 mb-5 mt-4">
          <img
            src="https://static.igem.wiki/teams/4888/wiki/engineering/predicted-ec20-alphafold.png"
            alt="ec20 alpha fold"
            id="figure-1-ec20-alpha-fold"
            style={{ height: "60%", width: "60%" }}
          />
          <figcaption className="text-center">
            <b>
              <u>Figure 1:</u>{" "}
            </b>
            The predicted structure was computed using the Google Colab
            Alphafold_2 notebook
            <a href="#reference2">
              <sup>2</sup>{" "}
            </a>
            . The parameters used were the following:{" "}
            <em>
              msa_method=mmseqs2, homooligomer=1, pair_mode=unpaired, cov=0,
              qid=0, max_msa=512:1024, subsample_msa=True, num_relax=None,
              use_turbo=True, use_ptm=True, rank_by=pLDDT, num_models=5,
              num_samples=1, num_ensemble=1, max_recycles=24, is_training=False,
              use_templates=False
            </em>
            . The model scores obtained were: pLDDT:85.88 and pTMscore:0.3968
          </figcaption>
        </figure>

        <p className="mb-2">
          The predicted structure of EC20 is a small hairpin protein reinforced
          by 9 disulfide bonds. It is composed of 41 amino acids and weighs 4.72
          kDa.
        </p>

        <p className="mb-2">
          The engineering cycle is an iterative process aimed at solving
          engineering problems despite gaps in knowledge. Through iteration of
          this cycle, one will achieve engineering success.
        </p>

        <p className="mb-2">
          In this section we describe how we went through iterations of the
          engineering cycle to equip our live biotherapeutic product of a
          cadmium catching apparatus. To be an optimal cadmium catching
          apparatus, EC20 should bind specifically with high affinity to cadmium
          ions and be expressed constitutively at the live bio therapeutic
          product’s surface.
        </p>

        <p className="mb-2">
          We can list the four aimed criteria of our engineered cadmium catching
          apparatus as:
        </p>

        <ul style={{ listStyle: "inside" }}>
          <li> Bind and capture cadmium </li>

          <li>Be present at the surface</li>

          <li>Have constitutive expression</li>

          <li>Have the highest affinity for cadmium</li>
        </ul>

        <h2 className="mt-20" id="cycle-1_e">
          Cycle 1
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <h3 className="mb-5" id="cycle-1-design_e">
          Design
        </h3>

        <p className="mb-2">
          Cadmium binding and affinity are intrinsic parameters of the cadmium
          catching protein. To achieve our first and fourth requirements, bind
          and capture cadmium, and have the highest affinity for cadmium, we did
          a literature review and chose the best suited cadmium catching protein
          for our purposes: EC20.
        </p>

        <p className="mb-2">
          We then needed to address how we could express EC20 on the bacterias
          surface to meet our second requirement. To achieve its role of cadmium
          catching apparatus, EC20 needs to be exposed to the intestine lumen to
          capture and store cadmium. We met this requirement by fusing EC20 to
          the well-known surface display protein Lpp-OmpA.
        </p>

        <p className="mb-2">
          Finally, we addressed our third requirement, to have constitutive
          expression of the cadmium catching protein. Our live biotherapeutic
          product should be able to catch cadmium during its entire stay in the
          intestine. This way, new EC20 proteins should appear during the
          systems action. We tried to meet this requirement by placing our
          fusion protein Lpp-OmpA-EC20 under the control of the E.coli
          constitutive promoter BBa_J23100.
        </p>
        <p className="mb-2">
          Additionally, we had to think about testing requirements. When
          designing our catching apparatus, we designed ways to check that the
          system works as expected. To check that EC20 is indeed expressed we
          added a 6XHis tag at its N-terminus. This 6XHis tag allows to
          specifically retrieve our fusion protein with the appropriate
          antibody.
        </p>

        <p className="mb-2">
          Third requirement: EC20 needs to be expressed constitutively
        </p>

        <figure className="flex flex-col justify-center items-center gap-4">
          <img
            src="https://static.igem.wiki/teams/4888/wiki/engineering/ec20.png"
            alt="ec20"
            id="figure-1-ec20"
            style={{ height: "60%", width: "60%" }}
          />
          <figcaption>
            <b>
              <u>Figure 1:</u>{" "}
            </b>
            EC20 Design
          </figcaption>
        </figure>
        <h3 className="mb-5 mt-10" id="cycle-1-build_e">
          Build
        </h3>

        <p className="mb-2">
          The engineered construct composed of the BBa_J23100 promoter, plus the
          fusion protein Lpp-OmpA+6XHis tag+EC20, was cloned into the pET-3a
          vector and synthetized by GenScript
          <a href="#reference3">
            <sup>3</sup>{" "}
          </a>
          , a member of the International Gene Synthesis Consortium. Our
          selected vector contains an ampicillin resistance gene for selection
          of carrier bacteria.
        </p>

        <p className="mb-2">
          We then transformed this plasmid into the competent lab strain E. Coli
          BL21 DE3, resulting in a testable engineered biological system.
        </p>

        <h3 className="mb-5 mt-10" id="cycle-1-test_e">
          Test
        </h3>

        <p className="mb-2">
          In this step of the engineering cycle, we intended to decipher if our
          cadmium catching apparatus design met our aimed criteria.
        </p>

        <p className="mb-2">
          We first investigated the constitutive expression criterium. We tested
          if our fusion protein Lpp-Ompa-EC20 is expressed via western blot
          using an anti-6XHis antibody.
        </p>
        <p className="mb-2">
          See corresponding experimental setup :{" "}
          <a href="/epfl/experiments#Checking-that-EC20-is-expressed_e">
            Checking that EC20 is expressed
          </a>
          .
        </p>

        <p className="mb-2">
          Next, we tested the surface expression requirement. We used two
          assays: a proteinase K digestion assay, and an immunofluorescence
          assay.
        </p>
        <p className="mb-2">
          See corresponding experimental setup :
          <a href="../epfl/experiments#Surface_e">
            {" "}
            Checking that EC20 is on the surface{" "}
          </a>
        </p>

        <p className="mb-2">
          In the proteinase K digestion assay, we used proteinase K digestion as
          the proxy for surface expression. Only proteins facing the external
          environment would be digested. Hence, we expected to see a decrease in
          EC20 quantity if expressed at the surface. The decrease was monitored
          via western blot.
        </p>

        <p className="mb-2">
          In the immunofluorescence assay, we used fluorescent antibodies
          directed towards the 6XHis tag as a marker. Under a fluorescence
          microscope, bacteria harboring EC20 at the surface would emit a
          stronger light signal.
        </p>

        <p className="mb-2">
          Finally, we investigated if EC20 met our cadmium binding and capture
          criterium. We used colorimetric assays to infer the binding capacities
          of EC20. For safety reasons, we first tested binding to Zinc, a
          cadmium-like bivalent cation. After we successfully measured zinc
          binding, we then tested the capability of EC20 to bind and capture
          cadmium. For both, we expected a stronger uptake of ions for EC20
          expressing bacteria compared to negative control bacteria.
        </p>
        <p className="mb-2">
          See corresponding experimental setup :
          <a href="../epfl/experiments#cadmium_e">
            {" "}
            Checking that EC20 binds to cadmium{" "}
          </a>
        </p>

        <h3 className="mb-5 mt-10" id="cycle-1-learn_e">
          Learn
        </h3>

        <p className="mb-2">
          From the above-mentioned experiments, we demonstrated that EC20 is
          constitutively expressed in our live biotherapeutic product and able
          to catch both zinc and cadmium ions. However, we could not conclude
          that EC20 is present at the surface, nor could we demonstrate that
          EC20 has a higher affinity for cadmium than other bivalent cations.
        </p>

        <p className="mb-2">
          After this first engineering cycle, two out of four required criteria
          for having an optimal cadmium catching apparatus were reached.
        </p>

        <p className="mb-2">
          Through one iteration of the engineering cycle, we demonstrated
          promising results so as to approach an optimal cadmium catching
          apparatus for our live biological therapeutic. Thereby making
          important steps towards engineering success.
        </p>
        <br />
        <h2 className="mt-20" id="cycle-2_e">
          Cycle 2
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <h3 className="mb-5" id="cycle-2-design_e">
          Design
        </h3>

        <p className="mb-2">
          Following engineering cycles should focus on correcting surface
          expression and demonstrating the higher affinity of EC20 to cadmium
          ions compared to other bivalent cations.
        </p>

        <p className="mb-2">
          By lack of time, we could not go through more engineering cycle
          iterations in our project. However, without time constraints we would
          investigate if another surface display protein would bring EC20 to the
          surface more successfully than Lpp-OmpA. We would also test EC20
          binding capacities in the presence of both cadmium and multiple
          bivalent cations, to demonstrate EC20 higher affinity to cadmium over
          other bivalent cations.
        </p>
        <p className="mb-2">
          Although time ran short, through one iteration of the engineering
          cycle, we demonstrated expression and functionality of EC20 as a
          system to bind and capture cadmium in a biological system. These
          promising results are an important steppingstone to engineering the
          optimal cadmium catching apparatus for our live biotherapeutic
          product. Thereby making important steps towards engineering success.
        </p>
        <br />
        <h2 className="mt-20" id="References_e">
          References
        </h2>
        <hr className="dividergeneral mb-5"></hr>
        <ul>
          <ol type="1">
            <li id="reference1">
              {" "}
              Bae, Weon, et al. <br />{" "}
              <a href="https://onlinelibrary.wiley.com/doi/epdf/10.1002/1097-0290%2820001205%2970%3A5%3C518%3A%3AAID-BIT6%3E3.0.CO%3B2-5">
                Enhanced bioaccumulation of heavy metals by bacterial cells
                displaying synthetic phytochelatins.
              </a>{" "}
              <br />
              Biotechnology and bioengineering 70.5 (2000): 518-524{" "}
            </li>
            <li id="reference2">
              {" "}
              <a href="https://alphafold.ebi.ac.uk/">AlphaFold</a>
              <br />
            </li>
            <li id="reference2">
              {" "}
              <a href="https://www.genscript.com/">Genscript</a>
              <br />
            </li>
          </ol>
        </ul>
      </div>
    </div>
  );
};

export default Article;
